import { useEffect, useState } from 'react';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import { Search, ArrowRight, Home, Building2, ShieldCheck, Handshake } from 'lucide-react';
import { fetchFeatured } from './portalApi';
import type { PropertyCardData, SiteInfo } from './portalApi';
import PropertyCard from './PropertyCard';

const TYPES = [
  { value: '', label: 'Todos os tipos' },
  { value: 'APARTMENT', label: 'Apartamento' },
  { value: 'HOUSE', label: 'Casa' },
  { value: 'COMMERCIAL', label: 'Comercial' },
  { value: 'LAND', label: 'Terreno' },
  { value: 'RURAL', label: 'Rural' },
];

export default function PortalHome() {
  const { site } = useOutletContext<{ site: SiteInfo | null }>();
  const navigate = useNavigate();
  const [purpose, setPurpose] = useState<'sale' | 'rent'>('sale');
  const [type, setType] = useState('');
  const [city, setCity] = useState('');
  const [q, setQ] = useState('');
  const [featured, setFeatured] = useState<{ rent: PropertyCardData[]; sale: PropertyCardData[] }>({ rent: [], sale: [] });

  useEffect(() => {
    fetchFeatured().then(setFeatured).catch(() => undefined);
  }, []);

  function search(e: React.FormEvent) {
    e.preventDefault();
    const params = new URLSearchParams();
    params.set('purpose', purpose);
    if (type) params.set('type', type);
    if (city) params.set('city', city);
    if (q) params.set('q', q);
    navigate(`/imoveis?${params.toString()}`);
  }

  return (
    <div>
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-primary-800 via-primary-700 to-primary-900 text-white">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_20%_20%,white,transparent_40%),radial-gradient(circle_at_80%_60%,white,transparent_35%)]" />
        <div className="relative max-w-7xl mx-auto px-4 py-20 md:py-28">
          <h1 className="text-3xl md:text-5xl font-bold leading-tight max-w-2xl">
            {site?.name || 'Imobiliária'} realizando sonhos
          </h1>
          <p className="mt-4 text-primary-100 text-lg max-w-xl">
            Encontre o imóvel ideal para comprar ou alugar. {site?.properties ? `${site.properties} imóveis` : 'Imóveis'} selecionados para você.
          </p>

          {/* Busca */}
          <form onSubmit={search} className="mt-8 bg-white rounded-2xl shadow-xl p-4 max-w-3xl text-gray-700">
            <div className="flex gap-2 mb-3">
              <button
                type="button"
                onClick={() => setPurpose('sale')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold ${
                  purpose === 'sale' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600'
                }`}
              >
                Comprar
              </button>
              <button
                type="button"
                onClick={() => setPurpose('rent')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold ${
                  purpose === 'rent' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600'
                }`}
              >
                Alugar
              </button>
            </div>
            <div className="grid md:grid-cols-[1fr_1fr_auto] gap-3">
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
              >
                {TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
              <input
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="Cidade ou bairro"
                className="px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
              />
              <button
                type="submit"
                className="flex items-center justify-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700"
              >
                <Search className="w-5 h-5" /> Buscar
              </button>
            </div>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por código, título ou palavra-chave..."
              className="mt-3 w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none"
            />
          </form>
        </div>
      </section>

      {/* Destaques de Venda */}
      <Showcase
        title="Imóveis à Venda"
        subtitle="Oportunidades selecionadas para você comprar"
        items={featured.sale}
        to="/imoveis?purpose=sale"
      />

      {/* Destaques de Locação */}
      <Showcase
        title="Imóveis para Alugar"
        subtitle="Os melhores imóveis para locação"
        items={featured.rent}
        to="/imoveis?purpose=rent"
        alt
      />

      {/* Diferenciais */}
      <section className="bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 py-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { icon: Home, t: 'Locação', d: 'Administração completa do seu imóvel para alugar com segurança.' },
            { icon: Building2, t: 'Venda', d: 'Compra e venda com avaliação justa e acompanhamento total.' },
            { icon: ShieldCheck, t: 'Segurança', d: 'Análise de crédito e contratos com respaldo jurídico.' },
            { icon: Handshake, t: 'Atendimento', d: 'Equipe pronta para te ajudar em cada etapa.' },
          ].map((f) => (
            <div key={f.t} className="text-center">
              <div className="mx-auto w-14 h-14 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center">
                <f.icon className="w-7 h-7" />
              </div>
              <p className="mt-3 font-semibold text-gray-800">{f.t}</p>
              <p className="mt-1 text-sm text-gray-500">{f.d}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function Showcase({
  title,
  subtitle,
  items,
  to,
  alt,
}: {
  title: string;
  subtitle: string;
  items: PropertyCardData[];
  to: string;
  alt?: boolean;
}) {
  if (!items?.length) return null;
  return (
    <section className={alt ? 'bg-gray-50' : ''}>
      <div className="max-w-7xl mx-auto px-4 py-14">
        <div className="flex items-end justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
            <p className="text-gray-500">{subtitle}</p>
          </div>
          <Link to={to} className="hidden sm:flex items-center gap-1 text-primary-600 font-semibold hover:text-primary-700">
            Ver todos <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((p) => (
            <PropertyCard key={p.id} p={p} />
          ))}
        </div>
        <div className="mt-6 sm:hidden">
          <Link to={to} className="flex items-center justify-center gap-1 text-primary-600 font-semibold">
            Ver todos <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
